import { useState } from "react"
import { Loader2, Search, Unplug } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  disconnectStripeAccount,
  discoverStripeDonors,
  type DiscoverStripeDonorsResponse,
} from "@/lib/stripe-connect"

interface StripeContinuityCardProps {
  orgId: string
  /** Called after a successful disconnect so the parent refreshes org data. */
  onDisconnected?: () => void
}

/**
 * NOS-194 (C2): shown when an org has connected an EXISTING Stripe account
 * via OAuth. Triggers the read-only recurring-donor discovery run and shows
 * its summary; also offers disconnect (to reconnect a different account).
 */
export function StripeContinuityCard({ orgId, onDisconnected }: StripeContinuityCardProps) {
  const [discovering, setDiscovering] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<DiscoverStripeDonorsResponse | null>(null)

  const handleDiscover = async () => {
    setError(null)
    setDiscovering(true)
    const { data, error: discoverError } = await discoverStripeDonors(orgId)
    setDiscovering(false)
    if (discoverError || !data) {
      setError(discoverError ?? "Discovery failed. Please try again.")
      return
    }
    setResult(data)
  }

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Disconnect this Stripe account? Donations will be unavailable until another account is connected. Existing subscriptions in Stripe are not affected."
      )
    ) {
      return
    }
    setError(null)
    setDisconnecting(true)
    const { error: disconnectError } = await disconnectStripeAccount(orgId)
    setDisconnecting(false)
    if (disconnectError) {
      setError(disconnectError)
      return
    }
    onDisconnected?.()
  }

  return (
    <Card data-testid="stripe-continuity-card">
      <CardHeader>
        <CardTitle className="text-base">Bring over your existing donors</CardTitle>
        <CardDescription>
          Your existing Stripe account is connected. Run a read-only scan to import your
          Stripe customers as contacts and adopt active recurring gifts — nothing is
          changed or charged in Stripe.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={handleDiscover} disabled={discovering || disconnecting}>
            {discovering ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Scanning Stripe...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                {result ? "Run discovery again" : "Find existing donors"}
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDisconnect}
            disabled={discovering || disconnecting}
          >
            {disconnecting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Disconnecting...
              </>
            ) : (
              <>
                <Unplug className="mr-2 h-4 w-4" />
                Disconnect
              </>
            )}
          </Button>
        </div>
        {result && (
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2" data-testid="discovery-summary">
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Stripe customers found</dt>
              <dd className="font-medium">{result.customers_found}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Contacts created</dt>
              <dd className="font-medium">{result.contacts_created}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Contacts updated</dt>
              <dd className="font-medium">{result.contacts_updated}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Recurring gifts adopted</dt>
              <dd className="font-medium">{result.subscriptions_adopted}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Saved card, no subscription</dt>
              <dd className="font-medium">{result.saved_pm_no_subscription.count}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">Skipped (no email)</dt>
              <dd className="font-medium">{result.skipped_no_email}</dd>
            </div>
          </dl>
        )}
        {result && result.subscriptions_adopted > 0 && (
          <p className="text-xs text-muted-foreground">
            Adopted recurring gifts will appear as donations automatically when each
            renewal is paid.
          </p>
        )}
        {result && result.saved_pm_no_subscription.count > 0 && (
          <p className="text-xs text-muted-foreground">
            {result.saved_pm_no_subscription.count} donor
            {result.saved_pm_no_subscription.count !== 1 ? "s have" : " has"} a saved
            payment method but no active Stripe subscription (usually plugin-managed
            recurring gifts, e.g. GiveWP). These can be migrated in a follow-up step.
          </p>
        )}
        {result?.truncated && (
          <p className="text-xs text-amber-700">
            Your Stripe account has more customers than a single scan covers — only the
            first 2,000 were scanned. Contact support to import the rest.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
